import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { apiClient } from '@/lib/api/client'
import { API_CONFIG } from '@/lib/config/api.config'
import type {
  TestSuite,
  TestSuitesResponse,
  CreateTestSuiteRequest,
  UpdateTestSuiteRequest,
} from '@/types/suite.types'

// Query keys
export const testSuiteKeys = {
  all: ['testsuites'] as const,
  lists: () => [...testSuiteKeys.all, 'list'] as const,
  list: (filters?: Record<string, unknown>) =>
    [...testSuiteKeys.lists(), filters] as const,
  details: () => [...testSuiteKeys.all, 'detail'] as const,
  detail: (id: number) => [...testSuiteKeys.details(), id] as const,
}

// List test suites with optional filtering
export const useTestSuites = (params?: {
  project_id?: number
  parent_id?: number
  limit?: number
  offset?: number
}) => {
  return useQuery({
    queryKey: testSuiteKeys.list(params),
    queryFn: async () => {
      const queryParams = new URLSearchParams()
      if (params?.project_id)
        queryParams.append('project_id', params.project_id.toString())
      if (params?.parent_id)
        queryParams.append('parent_id', params.parent_id.toString())
      if (params?.limit)
        queryParams.append('limit', params.limit.toString())
      if (params?.offset)
        queryParams.append('offset', params.offset.toString())

      const url = queryParams.toString()
        ? `${API_CONFIG.ENDPOINTS.TEST_SUITES.LIST}?${queryParams.toString()}`
        : API_CONFIG.ENDPOINTS.TEST_SUITES.LIST

      const { data } = await apiClient.get<TestSuitesResponse>(url)
      return data.suites
    },
  })
}

// Get single test suite by ID (includes children per openapi: "Get test suite by ID with children")
export const useTestSuite = (id: number) => {
  return useQuery({
    queryKey: testSuiteKeys.detail(id),
    queryFn: async () => {
      const { data } = await apiClient.get<TestSuite>(
        API_CONFIG.ENDPOINTS.TEST_SUITES.GET(id.toString())
      )
      return data
    },
    enabled: id > 0,
  })
}

// Create test suite — POST /api/suites → 201
export const useCreateSuite = () => {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: async (request: CreateTestSuiteRequest) => {
      const { data } = await apiClient.post<TestSuite>(
        API_CONFIG.ENDPOINTS.TEST_SUITES.CREATE,
        request
      )
      return data
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: testSuiteKeys.lists() })
    },
  })
}

// Update test suite — PUT /api/suites/{suite_id} → 200
export const useUpdateSuite = () => {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: async ({
      id,
      data: updateData,
    }: {
      id: number
      data: UpdateTestSuiteRequest
    }) => {
      const { data } = await apiClient.put<TestSuite>(
        API_CONFIG.ENDPOINTS.TEST_SUITES.UPDATE(id.toString()),
        updateData
      )
      return data
    },
    onSuccess: (_data, variables) => {
      queryClient.invalidateQueries({ queryKey: testSuiteKeys.lists() })
      queryClient.invalidateQueries({
        queryKey: testSuiteKeys.detail(variables.id),
      })
    },
  })
}

// Delete test suite — DELETE /api/suites/{suite_id} → 204 (cascades to children via FK)
export const useDeleteSuite = () => {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: async (id: number) => {
      await apiClient.delete(
        API_CONFIG.ENDPOINTS.TEST_SUITES.DELETE(id.toString())
      )
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: testSuiteKeys.lists() })
    },
  })
}
